/**
 * Form Builder Store
 *
 * Zustand store for managing form builder state including:
 * - Form definition and fields
 * - Edit mode and field selection
 * - Drag and drop state
 * - Undo/redo history
 * - UI modals and panels
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  FormDefinition,
  FormFieldDefinition,
  FormSettings,
  FieldType,
  ClipboardData,
} from '../components/advanced/form-builder/types';
import { DEFAULT_FORM_SETTINGS } from '../components/advanced/form-builder/types';
import { createDefaultField, MAX_HISTORY_SIZE } from '../components/advanced/form-builder/constants';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Generate a unique ID for fields
 */
function generateFieldId(): string {
  return `field_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Generate a unique ID for forms
 */
function generateFormId(): string {
  return `form_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Get current timestamp in ISO format
 */
function getTimestamp(): string {
  return new Date().toISOString();
}

/**
 * Create an empty form definition
 */
function createEmptyForm(name: string = 'Untitled Form'): FormDefinition {
  const now = getTimestamp();
  return {
    id: generateFormId(),
    name,
    description: '',
    fields: [],
    settings: { ...DEFAULT_FORM_SETTINGS },
    createdAt: now,
    updatedAt: now,
    version: 1,
  };
}

// ============================================================================
// Store Types
// ============================================================================

interface FormBuilderState {
  // Form data
  form: FormDefinition | null;

  // Edit mode
  isEditMode: boolean;
  selectedFieldId: string | null;
  selectedFieldIds: string[]; // For multi-select

  // Drag & drop
  draggedFieldType: FieldType | null;
  draggedFieldId: string | null;
  dropTargetIndex: number | null;
  isDraggingFromPalette: boolean;

  // History (undo/redo)
  history: FormDefinition[];
  historyIndex: number;

  // UI state
  showFieldSettings: boolean;
  showFormSettings: boolean;
  showPreview: boolean;
  sidebarTab: 'fields' | 'properties';

  // Clipboard
  clipboard: ClipboardData | null;

  // Persistence scope
  /**
   * When false the working form is NOT written to localStorage (editing a saved server
   * form — the server is the source of truth, and two forms must never overwrite each
   * other's local copy). The previously persisted local form is kept in
   * `persistedFormBackup` so the demo builder's draft survives and is restored.
   */
  persistForm: boolean;
  persistedFormBackup: FormDefinition | null;

  // CCMS context
  /** Member attributes a field may map to (FormsLookupsView.memberFields). Empty = hide CCMS mapping. */
  memberFieldOptions: string[];
}

interface FormBuilderActions {
  // Form management
  setForm: (form: FormDefinition) => void;
  createNewForm: (name?: string) => void;
  updateFormName: (name: string) => void;
  updateFormDescription: (description: string) => void;
  clearForm: () => void;

  // Field operations
  addField: (type: FieldType, atIndex?: number) => void;
  updateField: (id: string, updates: Partial<FormFieldDefinition>) => void;
  removeField: (id: string) => void;
  removeSelectedFields: () => void;
  duplicateField: (id: string) => void;
  reorderFields: (fromIndex: number, toIndex: number) => void;
  moveField: (fieldId: string, newIndex: number) => void;

  // Selection
  selectField: (id: string | null) => void;
  selectFields: (ids: string[]) => void;
  addToSelection: (id: string) => void;
  removeFromSelection: (id: string) => void;
  selectAllFields: () => void;
  clearSelection: () => void;

  // Drag & drop
  setDraggedFieldType: (type: FieldType | null) => void;
  setDraggedFieldId: (id: string | null) => void;
  setDropTargetIndex: (index: number | null) => void;
  setIsDraggingFromPalette: (isDragging: boolean) => void;
  handleDrop: (index: number) => void;

  // Settings
  updateFormSettings: (settings: Partial<FormSettings>) => void;

  // History
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  pushToHistory: () => void;

  // Mode toggles
  setEditMode: (enabled: boolean) => void;
  toggleEditMode: () => void;
  togglePreview: () => void;
  setShowFieldSettings: (show: boolean) => void;
  setShowFormSettings: (show: boolean) => void;
  setSidebarTab: (tab: 'fields' | 'properties') => void;

  // Clipboard operations
  copyField: (id: string) => void;
  copySelectedFields: () => void;
  pasteFields: (atIndex?: number) => void;
  cutField: (id: string) => void;

  // Export/Import
  exportFormJSON: () => string;
  importFormJSON: (json: string) => boolean;
  exportFormForRender: () => FormDefinition | null;

  // Persistence / context
  setPersistForm: (enabled: boolean) => void;
  setMemberFieldOptions: (fields: string[]) => void;

  // Utilities
  getFieldById: (id: string) => FormFieldDefinition | undefined;
  getFieldIndex: (id: string) => number;
  validateForm: () => { isValid: boolean; errors: string[] };
}

type FormBuilderStore = FormBuilderState & FormBuilderActions;

// ============================================================================
// Store Implementation
// ============================================================================

export const useFormBuilderStore = create<FormBuilderStore>()(
  persist(
    (set, get) => ({
      // Initial state
      form: null,
      isEditMode: true,
      selectedFieldId: null,
      selectedFieldIds: [],
      draggedFieldType: null,
      draggedFieldId: null,
      dropTargetIndex: null,
      isDraggingFromPalette: false,
      history: [],
      historyIndex: -1,
      showFieldSettings: false,
      showFormSettings: false,
      showPreview: false,
      sidebarTab: 'fields',
      clipboard: null,
      persistForm: true,
      persistedFormBackup: null,
      memberFieldOptions: [],

      // Form management
      setForm: (form) => {
        set({
          form,
          selectedFieldId: null,
          selectedFieldIds: [],
          history: [form],
          historyIndex: 0,
        });
      },

      createNewForm: (name = 'Untitled Form') => {
        const newForm = createEmptyForm(name);
        set({
          form: newForm,
          selectedFieldId: null,
          selectedFieldIds: [],
          history: [newForm],
          historyIndex: 0,
          isEditMode: true,
          showPreview: false,
        });
      },

      updateFormName: (name) => {
        const { form, pushToHistory } = get();
        if (!form) return;

        pushToHistory();
        set({
          form: {
            ...form,
            name,
            updatedAt: getTimestamp(),
          },
        });
      },

      updateFormDescription: (description) => {
        const { form, pushToHistory } = get();
        if (!form) return;

        pushToHistory();
        set({
          form: {
            ...form,
            description,
            updatedAt: getTimestamp(),
          },
        });
      },

      clearForm: () => {
        set({
          form: null,
          selectedFieldId: null,
          selectedFieldIds: [],
          history: [],
          historyIndex: -1,
        });
      },

      // Field operations
      addField: (type, atIndex) => {
        const { form, pushToHistory } = get();
        if (!form) return;

        pushToHistory();

        const fieldId = generateFieldId();
        const newField = createDefaultField(type, fieldId);
        const fields = [...form.fields];
        const insertIndex = atIndex !== undefined ? atIndex : fields.length;

        fields.splice(insertIndex, 0, newField);

        set({
          form: {
            ...form,
            fields,
            updatedAt: getTimestamp(),
          },
          selectedFieldId: fieldId,
          selectedFieldIds: [fieldId],
          sidebarTab: 'properties',
        });
      },

      updateField: (id, updates) => {
        const { form, pushToHistory } = get();
        if (!form) return;

        pushToHistory();

        const fields = form.fields.map((field) =>
          field.id === id ? { ...field, ...updates } : field
        );

        set({
          form: {
            ...form,
            fields,
            updatedAt: getTimestamp(),
          },
        });
      },

      removeField: (id) => {
        const { form, pushToHistory, selectedFieldId, selectedFieldIds } = get();
        if (!form) return;

        pushToHistory();

        const fields = form.fields.filter((field) => field.id !== id);

        set({
          form: {
            ...form,
            fields,
            updatedAt: getTimestamp(),
          },
          selectedFieldId: selectedFieldId === id ? null : selectedFieldId,
          selectedFieldIds: selectedFieldIds.filter((fid) => fid !== id),
        });
      },

      removeSelectedFields: () => {
        const { form, pushToHistory, selectedFieldIds } = get();
        if (!form || selectedFieldIds.length === 0) return;

        pushToHistory();

        const fields = form.fields.filter(
          (field) => !selectedFieldIds.includes(field.id)
        );

        set({
          form: {
            ...form,
            fields,
            updatedAt: getTimestamp(),
          },
          selectedFieldId: null,
          selectedFieldIds: [],
        });
      },

      duplicateField: (id) => {
        const { form, pushToHistory } = get();
        if (!form) return;

        const sourceField = form.fields.find((f) => f.id === id);
        if (!sourceField) return;

        pushToHistory();

        const fieldId = generateFieldId();
        const newField: FormFieldDefinition = {
          ...sourceField,
          id: fieldId,
          name: `${sourceField.name}_copy`,
          label: `${sourceField.label} (Copy)`,
        };

        const sourceIndex = form.fields.findIndex((f) => f.id === id);
        const fields = [...form.fields];
        fields.splice(sourceIndex + 1, 0, newField);

        set({
          form: {
            ...form,
            fields,
            updatedAt: getTimestamp(),
          },
          selectedFieldId: fieldId,
          selectedFieldIds: [fieldId],
        });
      },

      reorderFields: (fromIndex, toIndex) => {
        const { form, pushToHistory } = get();
        if (!form) return;
        if (fromIndex === toIndex) return;

        pushToHistory();

        const fields = [...form.fields];
        const [moved] = fields.splice(fromIndex, 1);
        fields.splice(toIndex, 0, moved);

        set({
          form: {
            ...form,
            fields,
            updatedAt: getTimestamp(),
          },
        });
      },

      moveField: (fieldId, newIndex) => {
        const { form, getFieldIndex, reorderFields } = get();
        if (!form) return;

        const currentIndex = getFieldIndex(fieldId);
        if (currentIndex === -1) return;

        reorderFields(currentIndex, newIndex);
      },

      // Selection
      selectField: (id) => {
        set({
          selectedFieldId: id,
          selectedFieldIds: id ? [id] : [],
          sidebarTab: id ? 'properties' : 'fields',
        });
      },

      selectFields: (ids) => {
        set({
          selectedFieldId: ids.length > 0 ? ids[0] : null,
          selectedFieldIds: ids,
          sidebarTab: ids.length > 0 ? 'properties' : 'fields',
        });
      },

      addToSelection: (id) => {
        const { selectedFieldIds } = get();
        if (selectedFieldIds.includes(id)) return;

        set({
          selectedFieldId: id,
          selectedFieldIds: [...selectedFieldIds, id],
        });
      },

      removeFromSelection: (id) => {
        const { selectedFieldIds } = get();
        const newIds = selectedFieldIds.filter((fid) => fid !== id);

        set({
          selectedFieldId: newIds.length > 0 ? newIds[0] : null,
          selectedFieldIds: newIds,
        });
      },

      selectAllFields: () => {
        const { form } = get();
        if (!form) return;

        const allIds = form.fields.map((f) => f.id);
        set({
          selectedFieldId: allIds.length > 0 ? allIds[0] : null,
          selectedFieldIds: allIds,
        });
      },

      clearSelection: () => {
        set({
          selectedFieldId: null,
          selectedFieldIds: [],
        });
      },

      // Drag & drop
      setDraggedFieldType: (type) => set({ draggedFieldType: type }),
      setDraggedFieldId: (id) => set({ draggedFieldId: id }),
      setDropTargetIndex: (index) => set({ dropTargetIndex: index }),
      setIsDraggingFromPalette: (isDragging) =>
        set({ isDraggingFromPalette: isDragging }),

      handleDrop: (index) => {
        const {
          draggedFieldType,
          draggedFieldId,
          isDraggingFromPalette,
          addField,
          moveField,
        } = get();

        if (isDraggingFromPalette && draggedFieldType) {
          addField(draggedFieldType, index);
        } else if (draggedFieldId) {
          moveField(draggedFieldId, index);
        }

        // Reset drag state
        set({
          draggedFieldType: null,
          draggedFieldId: null,
          dropTargetIndex: null,
          isDraggingFromPalette: false,
        });
      },

      // Settings
      updateFormSettings: (settings) => {
        const { form, pushToHistory } = get();
        if (!form) return;

        pushToHistory();

        set({
          form: {
            ...form,
            settings: { ...form.settings, ...settings },
            updatedAt: getTimestamp(),
          },
        });
      },

      // History
      undo: () => {
        const { history, historyIndex } = get();
        if (historyIndex <= 0) return;

        const newIndex = historyIndex - 1;
        set({
          form: history[newIndex],
          historyIndex: newIndex,
          selectedFieldId: null,
          selectedFieldIds: [],
        });
      },

      redo: () => {
        const { history, historyIndex } = get();
        if (historyIndex >= history.length - 1) return;

        const newIndex = historyIndex + 1;
        set({
          form: history[newIndex],
          historyIndex: newIndex,
          selectedFieldId: null,
          selectedFieldIds: [],
        });
      },

      canUndo: () => {
        const { historyIndex } = get();
        return historyIndex > 0;
      },

      canRedo: () => {
        const { history, historyIndex } = get();
        return historyIndex < history.length - 1;
      },

      pushToHistory: () => {
        const { form, history, historyIndex } = get();
        if (!form) return;

        // Remove any future history if we're not at the end
        const newHistory = history.slice(0, historyIndex + 1);

        // Add current form to history
        newHistory.push(JSON.parse(JSON.stringify(form)));

        // Limit history size
        if (newHistory.length > MAX_HISTORY_SIZE) {
          newHistory.shift();
        }

        set({
          history: newHistory,
          historyIndex: newHistory.length - 1,
        });
      },

      // Mode toggles
      setEditMode: (enabled) => set({ isEditMode: enabled }),
      toggleEditMode: () => set((state) => ({ isEditMode: !state.isEditMode })),
      togglePreview: () =>
        set((state) => ({
          showPreview: !state.showPreview,
          isEditMode: state.showPreview, // Toggle back to edit mode when exiting preview
        })),
      setShowFieldSettings: (show) => set({ showFieldSettings: show }),
      setShowFormSettings: (show) => set({ showFormSettings: show }),
      setSidebarTab: (tab) => set({ sidebarTab: tab }),

      // Clipboard operations
      copyField: (id) => {
        const { form } = get();
        if (!form) return;

        const field = form.fields.find((f) => f.id === id);
        if (!field) return;

        set({
          clipboard: {
            type: 'field',
            data: JSON.parse(JSON.stringify(field)),
            timestamp: Date.now(),
          },
        });
      },

      copySelectedFields: () => {
        const { form, selectedFieldIds } = get();
        if (!form || selectedFieldIds.length === 0) return;

        const fields = form.fields.filter((f) =>
          selectedFieldIds.includes(f.id)
        );

        set({
          clipboard: {
            type: 'fields',
            data: JSON.parse(JSON.stringify(fields)),
            timestamp: Date.now(),
          },
        });
      },

      pasteFields: (atIndex) => {
        const { form, clipboard, pushToHistory } = get();
        if (!form || !clipboard) return;

        pushToHistory();

        const insertIndex = atIndex !== undefined ? atIndex : form.fields.length;
        let newFields: FormFieldDefinition[] = [];

        if (clipboard.type === 'field') {
          const field = clipboard.data as FormFieldDefinition;
          newFields = [
            {
              ...field,
              id: generateFieldId(),
              name: `${field.name}_copy`,
            },
          ];
        } else {
          const fields = clipboard.data as FormFieldDefinition[];
          newFields = fields.map((field) => ({
            ...field,
            id: generateFieldId(),
            name: `${field.name}_copy`,
          }));
        }

        const allFields = [...form.fields];
        allFields.splice(insertIndex, 0, ...newFields);

        set({
          form: {
            ...form,
            fields: allFields,
            updatedAt: getTimestamp(),
          },
          selectedFieldIds: newFields.map((f) => f.id),
          selectedFieldId: newFields[0]?.id || null,
        });
      },

      cutField: (id) => {
        const { copyField, removeField } = get();
        copyField(id);
        removeField(id);
      },

      // Export/Import
      exportFormJSON: () => {
        const { form } = get();
        if (!form) return '{}';
        return JSON.stringify(form, null, 2);
      },

      importFormJSON: (json) => {
        try {
          const parsed = JSON.parse(json) as FormDefinition;

          // Basic validation
          if (!parsed.id || !parsed.name || !Array.isArray(parsed.fields)) {
            return false;
          }

          // Assign new IDs to imported form
          const newForm: FormDefinition = {
            ...parsed,
            id: generateFormId(),
            fields: parsed.fields.map((field) => ({
              ...field,
              id: generateFieldId(),
            })),
            settings: { ...DEFAULT_FORM_SETTINGS, ...parsed.settings },
            updatedAt: getTimestamp(),
          };

          set({
            form: newForm,
            selectedFieldId: null,
            selectedFieldIds: [],
            history: [newForm],
            historyIndex: 0,
          });

          return true;
        } catch {
          return false;
        }
      },

      exportFormForRender: () => {
        const { form } = get();
        return form ? JSON.parse(JSON.stringify(form)) : null;
      },

      // Persistence / context
      setPersistForm: (enabled) => {
        const { persistForm, form, persistedFormBackup } = get();
        if (enabled === persistForm) return;
        if (!enabled) {
          // Park the locally persisted (demo) form; the working form is now ephemeral.
          set({ persistForm: false, persistedFormBackup: form });
        } else {
          // Restore the parked local form so the demo builder picks up where it left off.
          set({
            persistForm: true,
            persistedFormBackup: null,
            form: persistedFormBackup,
            selectedFieldId: null,
            selectedFieldIds: [],
            history: persistedFormBackup ? [persistedFormBackup] : [],
            historyIndex: persistedFormBackup ? 0 : -1,
            showPreview: false,
          });
        }
      },

      setMemberFieldOptions: (fields) => set({ memberFieldOptions: fields }),

      // Utilities
      getFieldById: (id) => {
        const { form } = get();
        return form?.fields.find((f) => f.id === id);
      },

      getFieldIndex: (id) => {
        const { form } = get();
        return form?.fields.findIndex((f) => f.id === id) ?? -1;
      },

      validateForm: () => {
        const { form } = get();
        const errors: string[] = [];

        if (!form) {
          errors.push('No form loaded');
          return { isValid: false, errors };
        }

        if (!form.name.trim()) {
          errors.push('Form name is required');
        }

        if (form.fields.length === 0) {
          errors.push('Form must have at least one field');
        }

        // Check for duplicate field names
        const names = new Set<string>();
        for (const field of form.fields) {
          if (names.has(field.name)) {
            errors.push(`Duplicate field name: ${field.name}`);
          }
          names.add(field.name);
        }

        // Check for empty field names
        for (const field of form.fields) {
          if (!field.name.trim()) {
            errors.push(`Field "${field.label}" has no name`);
          }
        }

        return { isValid: errors.length === 0, errors };
      },
    }),
    {
      name: 'ccms_form_builder',
      partialize: (state) =>
        ({
          // Only the local (demo / unsaved) form is persisted — never a saved server form.
          form: state.persistForm ? state.form : state.persistedFormBackup,
          clipboard: state.clipboard,
        }) as unknown as FormBuilderStore,
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectForm = (state: FormBuilderStore) => state.form;
export const selectFields = (state: FormBuilderStore) => state.form?.fields ?? [];
export const selectFormSettings = (state: FormBuilderStore) => state.form?.settings ?? DEFAULT_FORM_SETTINGS;
export const selectIsEditMode = (state: FormBuilderStore) => state.isEditMode;
export const selectSelectedFieldId = (state: FormBuilderStore) => state.selectedFieldId;
export const selectSelectedFieldIds = (state: FormBuilderStore) => state.selectedFieldIds;
export const selectShowPreview = (state: FormBuilderStore) => state.showPreview;
export const selectShowFieldSettings = (state: FormBuilderStore) => state.showFieldSettings;
export const selectShowFormSettings = (state: FormBuilderStore) => state.showFormSettings;
export const selectSidebarTab = (state: FormBuilderStore) => state.sidebarTab;
export const selectCanUndo = (state: FormBuilderStore) => state.historyIndex > 0;
export const selectCanRedo = (state: FormBuilderStore) => state.historyIndex < state.history.length - 1;
export const selectFieldCount = (state: FormBuilderStore) => state.form?.fields.length ?? 0;
export const selectClipboard = (state: FormBuilderStore) => state.clipboard;
export const selectMemberFieldOptions = (state: FormBuilderStore) => state.memberFieldOptions;

// Drag & drop selectors
export const selectDraggedFieldType = (state: FormBuilderStore) => state.draggedFieldType;
export const selectDraggedFieldId = (state: FormBuilderStore) => state.draggedFieldId;
export const selectDropTargetIndex = (state: FormBuilderStore) => state.dropTargetIndex;
export const selectIsDraggingFromPalette = (state: FormBuilderStore) => state.isDraggingFromPalette;
export const selectIsDragging = (state: FormBuilderStore) =>
  state.draggedFieldType !== null || state.draggedFieldId !== null;

// Selected field selector
export const selectSelectedField = (state: FormBuilderStore) => {
  if (!state.selectedFieldId || !state.form) return null;
  return state.form.fields.find((f) => f.id === state.selectedFieldId) ?? null;
};
